import { DatePipe } from '@angular/common';
import { Component, computed, inject, input, output } from '@angular/core';
import { RouterModule } from '@angular/router';
import { SharedModule } from 'app/shared';
import { Notification, ReadState } from './notifications.model';


@Component({
    selector: 'notifications-item-menu',
    imports: [
        SharedModule,
    ],
    styles: [
        `
        .dropdown-item {
            border-bottom: 0;
            color: #373a3c;
            width: 221px;
            height: 35px;
            font-size: 11pt;
        }
        .dropdown-item:hover {
            background-color: #F5F5F5;
            color: #373a3c;
        }
        `,
    ],
    template: `
        <ui-dropdown-menu placement="right">
            <button class="dropdown-item" (click)="changeReadState()">
                {{ notification().read ? 'Mark as unread' : 'Mark as read' }}
            </button>
            <button class="dropdown-item" (click)="hideNotification()">
                Hide this notification
            </button>
            @if (notification().kind == 'folder') {
                <button class="dropdown-item" (click)="openFolder()">
                    Open folder
                </button>
            }
        </ui-dropdown-menu>
    `,
})
export class NotificationsItemMenuComponent {

    notification = input.required<Notification>();

    private host = inject(NotificationsItemComponent);

    changeReadState() {
        this.host.changeReadState();
    }

    hideNotification() {
        this.host.onHideNotification();
    }

    openFolder() {
        this.host.onOpenFolder();
    }
}


@Component({
    selector: 'notifications-item',
    imports: [
        DatePipe,
        SharedModule,
        RouterModule,
    ],
    styles: [
        `
        .notification {
            width: 100%;
            background-color: transparent;
        }
        .notification:hover {
            background-color: #F3F7FA;
        }
        .notification:hover .notification-header {
            text-decoration: underline;
        }
        .notification-header {
            font-weight: bold;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
        .notification-options-btn {
            width: 28px;
            height: 28px;
            background-color: #D9E5EF;
            border: none;
            border-radius: 14px;
            display: inline-block;
            visibility: hidden;
            outline: none;
        }
        .notification:hover .notification-options-btn,
        .notification-options-btn.show {
            visibility: visible;
        }
        .notification-read-state-btn {
            border: none;
            width: 14px;
            height: 14px;
            border-radius: 7px;
        }
        .notification-unread {
            background-color: #236ab2;
        }
        .notification-unread:hover {
            outline: 2px solid #CCCCCC;
        }
        .notification-read {
            background-color: transparent;
        }
        .notification-read:hover {
            background-color: #236ab2;
        }
        .notification-footer {
            font-size: small;
        }
        .notification-footer-divider {
            height: 12px;
            width: 1px;
            background-color: lightgrey;
        }
        .min-width-0 {
            min-width: 0;
        }
        @media screen and (max-width: 766px) {
            .notification:hover .notification-options-btn {
                display: none;
            }
            .notification-content {
                font-size: small;
                text-overflow: ellipsis;
                white-space: nowrap;
                overflow: hidden;
            }
        }
        .notification-btn {
            background: none;
            border: none;
            text-align: left;
            height: 100%;
            min-height: 90px;
        }
        `,
    ],
    template: `
        <div class="notification d-flex p-1">
            <a
                class="btn notification-btn d-flex flex-grow-1 flex-shrink-1 min-width-0"
                [routerLink]="routerLink()"
                (click)="onOpenNotificationDetail()">
                <div class="d-flex flex-column min-width-0 py-1">
                    <div
                        class="notification-header d-flex align-items-center"
                        [attr.title]="notification().collectionName.length > 45 ? notification().collectionName : null">
                        @if (notification().kind === 'folder') {
                            <img src="/assets/images/folder-black.svg" height="11" class="mr-1">
                        }
                        @else if (notification().kind === 'area') {
                            <img src="/assets/images/area.svg" height="11" class="mr-1">
                        }
                        {{ notification().collectionName }}
                    </div>
                    <div class="d-flex flex-column min-width-0">
                        <div class="notification-content" [innerHTML]="notification().content"></div>
                        <div class="notification-footer mt-auto">
                            {{ notification().createdAt | date: 'mediumDate' }}
                            <div class="notification-footer-divider ml-1 mr-1 ml-sm-2 mr-sm-2 d-inline-block"></div>
                            {{ notification().propertiesCount == 1 ? '1 Property' : notification().propertiesCount + ' Properties' }}
                        </div>
                    </div>
                </div>
            </a>
            <button
                #menu="dropdown"
                [uiDropdown]="notificationMenu"
                [inputs]="{ notification: notification() }"
                [dropdownToggle]="false"
                class="notification-options-btn m-1 flex-shrink-0"
                [class.show]="menu.isOpen()"
                tooltip="Notification properties"
                tooltipPlacement="right">
                <i class="fa fa-ellipsis-h"></i>
            </button>
            <button
                class="notification-read-state-btn m-1 flex-shrink-0"
                [class.notification-unread]="!notification().read"
                [class.notification-read]="notification().read"
                [tooltip]="notification().read ? 'Mark notification as unread' : 'Mark notification as read'"
                tooltipPlacement="left"
                (click)="changeReadState()">
            </button>
        </div>
    `,
})
export class NotificationsItemComponent {

    notification = input.required<Notification>();

    changeNotificationReadState = output<ReadState>();

    hideNotification = output<string>();

    openFolder = output<string>();

    clickNotificationDetail = output<void>();

    notificationMenu = NotificationsItemMenuComponent;

    routerLink = computed(() => {
        const n = this.notification();
        if (n.kind == 'folder') {
            return ['/dashboard', 'saved-properties', 'history', n.collectionId, n.tab];
        }
        if (n.kind == 'area') {
            return ['/dashboard', 'watched-area', n.collectionId, n.tab];
        }
    });

    changeReadState() {
        this.changeNotificationReadState.emit({
            notificationId: this.notification().notificationId,
            read: !this.notification().read,
        });
    }

    onHideNotification() {
        this.hideNotification.emit(this.notification().notificationId);
    }

    onOpenFolder() {
        this.openFolder.emit(this.notification().collectionId);
    }

    onOpenNotificationDetail() {
        if (!this.notification().read) {
            this.changeNotificationReadState.emit({
                notificationId: this.notification().notificationId,
                read: true,
            });
        }

        this.clickNotificationDetail.emit();
    }
}
